'use client';
import { useAudioPlayer } from '@/context/AudioPlayerContext';
import { VolumeX, Volume1, Volume2 } from 'lucide-react';
import { useRef, useState, useEffect } from 'react';
import styles from './AudioPlayer.module.css';
import { Shuffle, Repeat, Repeat1, Timer } from "lucide-react";
import TimerInlinePicker from "./TimerInlinePicker";
import Hls from 'hls.js';
import { CirclePlay, CirclePause } from "lucide-react";

export default function AudioPlayer() {
  const {
  isPlaying,
  setIsPlaying,
  pause,
  resume,
  finishPlayback,
} = useAudioPlayer();
  const { currentTrack } = useAudioPlayer();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  
  const { shuffleMode, setShuffleMode } = useAudioPlayer();
  const { trackQueue, setCurrentTrack, playTracks } = useAudioPlayer();
  const { repeatMode, setRepeatMode } = useAudioPlayer();
  const { sleepTimer, setSleepTimer } = useAudioPlayer();
  const [isTimerMenuOpen, setIsTimerMenuOpen] = useState(false);
  const timerRef = useRef<HTMLDivElement>(null);
  const [, setSelectedTimer] = useState<number | null>(null);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const { sleepTimerStart, setSleepTimerStart } = useAudioPlayer();
  function shuffleArray<T>(array: T[]): T[] {
    const result = [...array];
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (timerRef.current && !timerRef.current.contains(e.target as Node)) {
        setIsTimerMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!sleepTimer) return;

    const timeout = setTimeout(() => {
      pause();            // ⏸ остановка + логирование аналитики
      setSleepTimer(null); // сброс таймера
    }, sleepTimer * 1000);

    return () => clearTimeout(timeout);
  }, [sleepTimer, pause]);


  const handleNextTrack = () => {
    if (!currentTrack || !trackQueue.length) return;
    const currentIndex = trackQueue.findIndex(t =>
      currentTrack?._instanceId
        ? t._instanceId === currentTrack._instanceId
        : t.id === currentTrack.id
    );

    const next = trackQueue[currentIndex + 1];
    if (next) {
      setCurrentTrack(next);
      audioRef.current?.play();
      setIsPlaying(true);
    }
  };

  const handlePrevTrack = () => {
    if (!currentTrack || !trackQueue.length) return;
    const currentIndex = trackQueue.findIndex(t =>
      currentTrack?._instanceId
        ? t._instanceId === currentTrack._instanceId
        : t.id === currentTrack.id
    );

    const prev = trackQueue[currentIndex - 1];
    if (prev) {
      setCurrentTrack(prev);
      audioRef.current?.play();
      setIsPlaying(true);
    }
  };
  const toggleRepeatMode = () => {
    if (repeatMode === 'none') setRepeatMode('track');
    else if (repeatMode === 'track') setRepeatMode('playlist');
    else setRepeatMode('none');
  };

  const VolumeIcon = ({ volume, onClick }) => {
    if (volume === 0) return <VolumeX className={styles.volumeIcon} onClick={onClick} />;
    if (volume < 0.5) return <Volume1 className={styles.volumeIcon} onClick={onClick} />;
    return <Volume2 className={styles.volumeIcon} onClick={onClick} />;
  };
  const toggleMute = () => {
    if (!audioRef.current) return;

    if (volume === 0) {
      audioRef.current.volume = 0.8;      // верни громкость обратно
      setVolume(0.8);
    } else {
      audioRef.current.volume = 0;
      setVolume(0);
    }
  };
  const toggleShuffle = () => {
    const newShuffle = !shuffleMode;
    setShuffleMode(newShuffle);

    if (newShuffle && trackQueue.length > 0) {
      // оставляем текущий трек
      const current = currentTrack;
      const rest = trackQueue.filter(t =>
        current?._instanceId
          ? t._instanceId !== current._instanceId
          : t.id !== current?.id
      );

      // перемешиваем остальные
      const shuffled = shuffleArray(rest);

      // собираем новую очередь: текущий + перемешанные
      const newQueue = [current, ...shuffled];

      // запускаем её через playTracks
      playTracks(newQueue);
      setCurrentTrack(current); // оставляем текущий трек
    }
  };



  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;

    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(`/api/audio/${currentTrack.slug}/${currentTrack.slug}.m3u8`);
      // ссылка на .m3u8
      hls.attachMedia(audio);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        audio.play().catch(err => console.warn('Ошибка при запуске HLS:', err));
        setIsPlaying(true);
      });

      return () => {
        hls.destroy(); // очищаем при смене трека
      };
    } else {
      // Safari умеет HLS нативно
      audio.src = currentTrack.audioUrl;
      audio.play().catch(err => console.warn('Ошибка при запуске аудио:', err));
      setIsPlaying(true);
    }
  }, [currentTrack]);



  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const update = () => {
      setProgress(audio.currentTime);
      setDuration(audio.duration || 0);
    };

    audio.addEventListener('timeupdate', update);
    return () => audio.removeEventListener('timeupdate', update);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;

    if (isPlaying) {
      audio.play().catch((err) => {
        console.warn('Ошибка при запуске аудио:', err);
      });
    } else {
      audio.pause();
    }
  }, [isPlaying]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;

    const handleEnded = () => {
      const currentIndex = trackQueue.findIndex(t =>
        currentTrack?._instanceId
          ? t._instanceId === currentTrack._instanceId
          : t.id === currentTrack.id
      );

      if (repeatMode === 'track') {
        audio.currentTime = 0;
        audio.play();
      } else if (repeatMode === 'playlist') {
        const next = trackQueue[currentIndex + 1] || trackQueue[0];
        setCurrentTrack(next);
        audio.play();
      } else {
  const next = trackQueue[currentIndex + 1];

  if (next) {
    setCurrentTrack(next);
    audio.play();
  } else {
    finishPlayback();
  }
}
    };


    audio.addEventListener('ended', handleEnded);
    return () => audio.removeEventListener('ended', handleEnded);
  }, [currentTrack, repeatMode, trackQueue]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      pause(); // ⏸ логируем и стопаем таймер
    } else {
      resume(); // ▶️ возобновляем воспроизведение и таймер
    }
  };



  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = value;
    }
    setProgress(value);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    setVolume(value);
    if (audioRef.current) {
      audioRef.current.volume = value;
    }
  };
  const formatTime = (time: number) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };
  const isTimerSet = sleepTimer !== null;

  let remainingTimeText = '';
  if (sleepTimer && sleepTimerStart) {
    const elapsed = Math.floor((Date.now() - sleepTimerStart) / 1000);
    const remaining = Math.max(sleepTimer - elapsed, 0);
    const hrs = Math.floor(remaining / 3600);
    const mins = Math.floor((remaining % 3600) / 60);
    remainingTimeText = `Time left: ${hrs > 0 ? `${hrs}h ` : ''}${mins}min`;
  }
  //if (!currentTrack) return null;

  const getPlayerImageSrc = (imageUrl?: string) => {
    if (!imageUrl) return '/images/placeholder.jpg';

    return imageUrl.replace(
      '/covers/',
      '/covers/220/'
    );
  };
  return (
    <div className={styles.playerContainer}>
      <audio ref={audioRef} />

      <div className={styles.top}>
        {/* Левая часть: только картинка */}
        <div className={styles.left}>
          {currentTrack?.imageUrl ? (
            <img
              src={getPlayerImageSrc(currentTrack.imageUrl)}
              alt={currentTrack.title}
              className={styles.trackImage}
            />
          ) : (
            <img
              src="/images/placeholder.jpg"
              alt=""
              className={styles.trackImage}
            />
          )}

          <div className={styles.title}>
            <div className={styles.trackTitle}>
              {currentTrack ? currentTrack.title : 'Select track'}
            </div>
            {currentTrack?.composer && (
              <div className={styles.composer}>
                {currentTrack.composer}
              </div>
            )}
          </div>
        </div>


        {/* Центр: кнопки + прогресс */}
        <div className={styles.center}>
          <div className={styles.controls}>
            <button className={styles.shuffleButton} onClick={toggleShuffle}>
              <Shuffle
                className={
                  shuffleMode ? styles.shuffleActive : styles.shuffleDisabled
                }
              />
            </button>

            <button className={styles.skipButton} onClick={handlePrevTrack}>
              <img src="/icons/prev.png" alt="Prev" />
            </button>
            <button className={styles.playButton} onClick={togglePlay}>
              <img
                src={isPlaying ? "/icons/pause100-new.png" : "/icons/play-new.png"}
                alt="Play"
              />
            </button>
            <button className={styles.skipButton} onClick={handleNextTrack}>
              <img src="/icons/next-new.png" alt="Next" />
            </button>

            <button className={styles.repeatButton} onClick={toggleRepeatMode}>
              {repeatMode === "track" ? (
                <Repeat1 className={styles.repeatTrack} />
              ) : repeatMode === "playlist" ? (
                <Repeat className={styles.repeatActive} />
              ) : (
                <Repeat className={styles.repeatDisabled} />
              )}
            </button>

          </div>

          <div className={styles.progressBar}>
            <span className={styles.time}>{formatTime(progress)}</span>
            <input
              type="range"
              min="0"
              max={duration}
              value={progress}
              onChange={handleSeek}
              className={styles.slider}
              style={{
                background: `linear-gradient(to right, #797979 ${(progress / duration) * 100}%, #ddd ${(progress / duration) * 100}%)`
              }}
            />
            <span className={styles.time}>{formatTime(duration)}</span>
          </div>
        </div>
        <div ref={timerRef} className={styles.timerWrapper}>
          <button
            className={`${styles.timerIcon} ${isTimerSet ? styles.timerIcon_active : ""}`}
            onClick={() => setIsTimerMenuOpen((prev) => !prev)}
            onDoubleClick={() => {
              // 🔹 Сброс таймера
              setSleepTimer(null);
              setSleepTimerStart(null);
              setSelectedTimer(null);
              setIsTimerMenuOpen(false);
            }}

          >
            <Timer size={25} strokeWidth={2} />
            <span className={styles.tooltip}>
              {isTimerSet ? remainingTimeText : "Set sleep timer"}
            </span>
          </button>

          {isTimerMenuOpen && (
            <div
              className={styles.timerMenu}
              onClick={(e) => e.stopPropagation()} // предотвращаем схлопывание
            >
              <TimerInlinePicker
                hours={hours}
                minutes={minutes}
                setHours={setHours}
                setMinutes={setMinutes}
                onSet={() => {
                  const totalSeconds = hours * 3600 + minutes * 60;
                  if (totalSeconds > 0) {
                    setSleepTimer(totalSeconds);
                    setSleepTimerStart(Date.now());
                    setSelectedTimer(totalSeconds);
                    setIsTimerMenuOpen(false);
                  }
                }}
              />
            </div>
          )}

        </div>

        {/* Правая часть: громкость + таймер */}
        <div className={styles.right}>
          <div className={styles.volume}>
            <VolumeIcon volume={volume} onClick={toggleMute} />
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={handleVolumeChange}
              disabled={!currentTrack}
              style={{ "--value-percent": `${volume * 100}%` } as React.CSSProperties}
            />
          </div>
        </div>
      </div>

      {/* 🔹 Новый блок: название и композитор на всю ширину */}
      <div className={styles.trackInfo}>
        {currentTrack ? (
          <div className={styles.trackLine}>
            {currentTrack?.composer && (
              <span className={styles.composer}>{currentTrack.composer}:</span>
            )}
            <span className={styles.trackTitle}>{currentTrack.title}</span>
          </div>
        ) : (
          <div className={styles.trackLine}>Select track</div>
        )}
      </div>
    </div>
  );

}
